import {
  CorrespondenceDrawer,
  CorrespondencePanel,
  ThreadView,
} from "@platform/correspondence-ui";
import { ErrorBoundary } from "@platform/design-system";
import { useUser } from "@platform/user/client";
import { useState } from "react";

import { useCorrespondenceDrafts } from "./useCorrespondenceDrafts";
import { useDiscardCorrespondenceDraft } from "./useDiscardCorrespondenceDraft";
import { useCorrespondenceThread } from "./useCorrespondenceThread";
import { useMarkCorrespondenceRead } from "./useMarkCorrespondenceRead";
import { useCorrespondenceThreads } from "./useCorrespondenceThreads";

export interface CorrespondenceDrawerContainerProps {
  open: boolean;
  onClose: () => void;
  /** The RFP's canonical identifier (UUID), which the listing is keyed by. */
  rfpCanonicalIdentifier: string | undefined;
  /** Client and broker, shown under the drawer title. */
  subtitle?: string;
}

/**
 * Hangs the correspondence drawer off the RFP workbench.
 *
 * Owns the fetching and the open/expanded state so `correspondence-ui` stays
 * renderable without a query client, which is what lets its Storybook stories
 * and tests run against plain data.
 */
export const CorrespondenceDrawerContainer = ({
  open,
  onClose,
  rfpCanonicalIdentifier,
  subtitle,
}: CorrespondenceDrawerContainerProps): React.JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const { user } = useUser();

  const { threads, isLoading, isError, refetch, loadMore, isLoadingMore } =
    useCorrespondenceThreads(open ? rfpCanonicalIdentifier : undefined);

  const {
    drafts,
    isLoading: isLoadingDrafts,
    isError: isDraftsError,
  } = useCorrespondenceDrafts(open ? rfpCanonicalIdentifier : undefined);

  const { discard, discardingId } = useDiscardCorrespondenceDraft();

  return (
    <CorrespondenceDrawer
      open={open}
      expanded={expanded}
      subtitle={subtitle}
      onClose={onClose}
      // Nowhere to minimize to yet — the drawer is the only surface
      // correspondence has. Closing is the honest behaviour until there is a
      // docked state to restore from.
      onMinimize={onClose}
      onToggleExpanded={() => setExpanded((was) => !was)}
    >
      {/*
        A failure inside the panel closes the conversation, not the RFP page
        the drawer is sitting on top of.
      */}
      <ErrorBoundary>
        <CorrespondencePanel
          threads={threads}
          isLoading={isLoading}
          isError={isError}
          onRetry={refetch}
          onLoadMore={loadMore}
          isLoadingMore={isLoadingMore}
          // Composing is not built yet (CD-2989 carries the send path, and
          // SendReply still answers 501), so the rail's pencil does nothing
          // rather than opening a composer that cannot send.
          onCompose={() => {}}
          drafts={drafts}
          isLoadingDrafts={isLoadingDrafts}
          isDraftsError={isDraftsError}
          onDiscardDraft={(draft) => discard(draft.threadId, draft.id)}
          discardingDraftId={discardingId}
          renderThreadBody={(threadId, collapse) => (
            <OpenThread
              threadId={threadId}
              viewerAddress={
                user.status === "signedIn" ? user.email : undefined
              }
              onCollapse={collapse}
            />
          )}
        />
      </ErrorBoundary>
    </CorrespondenceDrawer>
  );
};

interface OpenThreadProps {
  threadId: string;
  viewerAddress?: string;
  onCollapse: () => void;
}

/**
 * The thread under the row that was opened.
 *
 * Its own component so the read is a hook rather than a fetch the drawer
 * fires: mounting on expansion is what keys the query to one thread, and
 * unmounting on collapse is what stops a closed row holding a request open.
 */
const OpenThread = ({
  threadId,
  viewerAddress,
  onCollapse,
}: OpenThreadProps): React.JSX.Element => {
  const { messages, events, writable, isLoading, isError, refetch } =
    useCorrespondenceThread(threadId);
  const { setMessageRead, markThreadRead, isMarking } =
    useMarkCorrespondenceRead(threadId);

  return (
    <ThreadView
      messages={messages}
      events={events}
      viewerAddress={viewerAddress}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      onCollapse={onCollapse}
      // Marking read is a statement about having read the thread, so it is
      // offered only to a caller the service would accept one from.
      onSetRead={writable ? setMessageRead : undefined}
      onMarkThreadRead={writable ? markThreadRead : undefined}
      isMarking={isMarking}
    />
  );
};
